import { getEnvVariable } from '@appcharge/shared-ui';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios, { baseURLPromise } from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';
import { Report } from '../views/Reports/types/report.types';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

const BASE_URL =
  localStorageUtil.getAny(ELocalStorageKeys.ENVIRONMENT) ||
  getEnvVariable('REACT_APP_API_BASE_URL');

export default function useReports(projects?: string) {
  const axios = useAxios();
  const reportsRoute = EApiRoutes.FINANCIAL_REPORTS;

  const getReports = useQuery({
    ...fetchConfig.list,
    refetchInterval: 1000 * 5,
    refetchIntervalInBackground: false,
    retry: false,
    queryKey: [EQueryKeys.FINANCIAL_REPORTS],
    queryFn: async () => {
      await baseURLPromise();
      return await axios.get(
        getApiRouteOrMock(reportsRoute),
        undefined,
        projects ? { 'x-publishers': projects } : undefined
      );
    }
  });

  const getReportDownload = (reportId: string) => {
    baseURLPromise().then(() => {
      window.open(
        `${BASE_URL}${getApiRouteOrMock(reportsRoute)}/${reportId}/download`,
        '_blank'
      );
    });
  };

  const createReport = useMutation(
    async (params: { data: Pick<Report, 'interval'>; projects?: string }) => {
      const reportsParams = params.data.interval;
      await baseURLPromise();
      return await axios.post(
        getApiRouteOrMock(reportsRoute),
        reportsParams,
        params?.projects ? { 'x-publishers': params?.projects } : undefined
      );
    },
    {}
  );

  const deleteReport = useMutation(async (reportId: string) => {
    await baseURLPromise();
    return await axios.del(`${getApiRouteOrMock(reportsRoute)}/${reportId}`);
  }, {});

  return {
    getReports,
    createReport,
    deleteReport,
    getReportDownload
  };
}
